import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Sparkles, Send, BookOpen, Ban, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VEDA_ACCENTS = {
  rig:     { light: "#FDECC4", border: "#B08A3E", text: "#5B3A0F", tag: "#8B4513" },
  yajur:   { light: "#F4E4D6", border: "#A85B2E", text: "#5A2A0F", tag: "#A85B2E" },
  sama:    { light: "#E8F0DD", border: "#5C8A3F", text: "#25451A", tag: "#5C8A3F" },
  atharva: { light: "#E4DEEF", border: "#7A5AA6", text: "#2F1F55", tag: "#7A5AA6" },
};

export default function VedasPage() {
  // ------------------------------------------------------------------
  // AI Hymn Prescription — user types their problem, the backend
  // returns the SINGLE best hymn from EACH of the 4 Vedas (or an
  // honest "no solution in this Veda" when nothing matches). Only
  // ONE lean AI call is made to produce the meanings — minimum credits.
  // ------------------------------------------------------------------
  const [problem, setProblem] = useState("");
  const [rx, setRx] = useState(null);
  const [rxBusy, setRxBusy] = useState(false);
  const [rxErr, setRxErr] = useState("");
  const [deep, setDeep] = useState({});  // { hymn_id: {loading, data} }

  const askVedas = async () => {
    setRxErr(""); setRxBusy(true); setRx(null); setDeep({});
    try {
      const r = await axios.post(`${API}/api/vedas/prescribe`, { problem });
      setRx(r.data);
    } catch (ex) {
      setRxErr(ex?.response?.data?.detail || "Could not fetch hymns.");
    } finally { setRxBusy(false); }
  };

  const expandDeep = async (hymn_id) => {
    if (deep[hymn_id]?.data) return;
    setDeep((d) => ({ ...d, [hymn_id]: { loading: true } }));
    try {
      const r = await axios.post(`${API}/api/vedas/expand`,
        { hymn_id, problem });
      setDeep((d) => ({ ...d, [hymn_id]: { loading: false, data: r.data } }));
    } catch (ex) {
      setDeep((d) => ({ ...d, [hymn_id]: { loading: false } }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF3DC] text-[#3A2A0F]">
      {/* header */}
      <header className="border-b border-[#B08A3E]/40 bg-gradient-to-r from-[#8B5A00] via-[#E5C158] to-[#8B5A00] sticky top-0 shadow-md z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" data-testid="vedas-back-link"
                className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-[#3A2A0F] hover:text-white">
            <ArrowLeft size={12} /> Dashboard
          </Link>
          <div className="text-center">
            <div className="text-[9px] uppercase tracking-[0.4em] font-mono text-[#3A2A0F]/80">Vedas Hall</div>
            <div className="font-heading text-xl text-[#3A2A0F]">Rig · Yajur · Sama · Atharva</div>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* --------- Ask-A-Hymn panel --------- */}
        <section className="border border-[#D4AF37]/50 rounded-md p-5 space-y-3 bg-gradient-to-br from-[#FBF3DC] to-[#F0E6C8] shadow-md"
                 data-testid="vedas-ask-panel">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#B08A3E]" />
            <div className="text-[10px] uppercase tracking-[0.35em] font-mono text-[#8B4513]">
              Ask a Vedic hymn for your problem
            </div>
          </div>
          <div className="text-[11px] font-mono text-[#5B4A2A]">
            Describe your situation. The engine searches ALL four Vedas
            and returns the single best-matching hymn from each. If no
            match exists in a particular Veda, it will honestly say so
            — nothing is fabricated. Verses are shown verbatim from the
            bundled corpus.
          </div>
          <textarea value={problem} onChange={(e) => setProblem(e.target.value)}
                     data-testid="vedas-problem"
                     rows={3} placeholder="e.g. I struggle with fear of debts and sleepless nights…"
                     className="w-full bg-white/70 border border-[#B08A3E]/40 focus:border-[#B08A3E] outline-none text-[#3A2A0F] font-mono text-sm px-3 py-2 rounded-sm" />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] font-mono text-[#8B4513]">
              Searching <span className="uppercase tracking-widest">all four Vedas</span>
            </div>
            <button onClick={askVedas} disabled={rxBusy || problem.trim().length < 10}
                    data-testid="vedas-ask-btn"
                    className="bg-gradient-to-r from-[#8B4513] to-[#B08A3E] text-white font-mono text-xs uppercase tracking-widest px-4 py-2 rounded-sm hover:brightness-110 disabled:opacity-50 flex items-center gap-2">
              {rxBusy ? <><Loader2 size={12} className="animate-spin" /> Reading the Vedas…</>
                        : <><Send size={12} /> Find my hymns</>}
            </button>
          </div>
          {rxErr && <div className="text-red-700 text-xs font-mono" data-testid="vedas-ask-error">{rxErr}</div>}
        </section>

        {/* --------- 4-Veda results grid --------- */}
        {rx && (
          <div className="grid gap-4 md:grid-cols-2" data-testid="vedas-rx-grid">
            {(rx.results || []).map((r) => {
              const acc = VEDA_ACCENTS[r.veda] || VEDA_ACCENTS.rig;
              if (!r.found) {
                return (
                  <div key={r.veda}
                       data-testid={`vedas-rx-${r.veda}`}
                       className="rounded-md p-5 border bg-white/60 shadow-sm"
                       style={{ borderColor: `${acc.border}55` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Ban size={14} style={{ color: acc.tag }} />
                      <div className="text-[10px] uppercase tracking-[0.35em] font-mono"
                           style={{ color: acc.tag }}>{r.veda_name}</div>
                    </div>
                    <div className="text-sm italic text-[#5B4A2A]"
                         data-testid={`vedas-rx-${r.veda}-none`}>
                      {r.message}
                    </div>
                  </div>
                );
              }
              return (
                <div key={r.veda}
                     data-testid={`vedas-rx-${r.veda}`}
                     className="rounded-md p-5 border shadow-sm space-y-3"
                     style={{ background: acc.light, borderColor: acc.border }}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <BookOpen size={14} style={{ color: acc.tag }} />
                      <div className="text-[10px] uppercase tracking-[0.35em] font-mono"
                           style={{ color: acc.tag }}>
                        {r.veda_name} · {r.hymn.reference}
                      </div>
                    </div>
                    <div className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border"
                         style={{ color: acc.tag, borderColor: `${acc.border}80` }}>
                      Deity · {r.hymn.deity}
                    </div>
                  </div>
                  <div className="font-heading text-lg leading-tight"
                       style={{ color: acc.text }}>
                    {r.hymn.hymn_name}
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-mono mb-1"
                         style={{ color: acc.tag }}>
                      Complete hymn (verbatim from source)
                    </div>
                    <div className="bg-white/80 border rounded-sm p-3 text-sm font-mono whitespace-pre-wrap max-h-[280px] overflow-y-auto"
                         style={{ color: acc.text, borderColor: `${acc.border}55` }}
                         data-testid={`vedas-rx-${r.veda}-body`}>
                      {r.hymn.body}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-widest font-mono mb-1"
                         style={{ color: acc.tag }}>
                      Meaning
                    </div>
                    <div className="text-sm leading-relaxed whitespace-pre-wrap"
                         style={{ color: acc.text }}
                         data-testid={`vedas-rx-${r.veda}-meaning`}>
                      {r.meaning}
                    </div>
                  </div>

                  {/* Unlock verse-by-verse deep commentary — on demand */}
                  {(() => {
                    const dstate = deep[r.hymn.hymn_id] || {};
                    if (dstate.data) {
                      return (
                        <div className="border-t pt-3"
                             style={{ borderColor: `${acc.border}55` }}
                             data-testid={`vedas-rx-${r.veda}-deep`}>
                          <div className="text-[9px] uppercase tracking-widest font-mono mb-2"
                               style={{ color: acc.tag }}>
                            Deeper reading · verse-by-verse
                          </div>
                          <div className="bg-white/80 border rounded-sm p-3 text-sm whitespace-pre-wrap"
                               style={{ color: acc.text, borderColor: `${acc.border}55` }}>
                            {dstate.data.deep_meaning}
                          </div>
                          {dstate.data.classical_how_to_chant && (
                            <div className="mt-2 text-[11px] italic"
                                 style={{ color: acc.text }}>
                              <span className="font-mono uppercase tracking-widest text-[9px] mr-1"
                                    style={{ color: acc.tag }}>
                                Classical chant note ·
                              </span>
                              {dstate.data.classical_how_to_chant}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <button onClick={() => expandDeep(r.hymn.hymn_id)}
                              disabled={dstate.loading}
                              data-testid={`vedas-rx-${r.veda}-expand-btn`}
                              className="border rounded-sm px-3 py-2 text-[10px] uppercase tracking-widest font-mono flex items-center gap-2 hover:brightness-95 disabled:opacity-50"
                              style={{ background: "white", borderColor: acc.border, color: acc.tag }}>
                        {dstate.loading
                          ? <><Loader2 size={12} className="animate-spin" /> Unlocking…</>
                          : <><ChevronDown size={12} /> Unlock verse-by-verse meaning · ≈ ₹5</>}
                      </button>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}

        {rx && (
          <div className="text-[10px] italic font-mono text-[#5B4A2A] text-center"
               data-testid="vedas-rx-source-note">
            {rx.source_note}
          </div>
        )}
      </div>
    </div>
  );
}
